import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { RedirectIfAuthenticated } from '@/features/auth/RedirectIfAuthenticated';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { CampaignsPage } from '@/features/campaigns/CampaignsPage';
import { CatalogoPage } from '@/features/catalogo/CatalogoPage';
import { CrmPage } from '@/features/crm/CrmPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { HelpPage } from '@/features/help/HelpPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { PaymentMethodsTab } from '@/features/settings/PaymentMethodsTab';
import { SettingsGeneralTab } from '@/features/settings/SettingsGeneralTab';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { TeamTab } from '@/features/settings/TeamTab';
import { TemplatesPage } from '@/features/templates/TemplatesPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ROUTES } from './paths';
import { RootRedirect } from './RootRedirect';

/**
 * Rutas de la aplicación.
 *
 * Estructura:
 *   /                → decide entre panel y login
 *   /login /register → `RedirectIfAuthenticated` + layout público (login y registro, paso 2)
 *   resto            → `RequireAuth` (exige JWT válido) + `AppLayout` (sidebar + header)
 *
 * Estado del plan (`docs/FRONTEND_PLAN.md` §4): COMPLETO. Están los pasos 1 (shell), 2
 * (login/registro), 3 (`/settings` + `/integrations`), 4 (`/products`), 5 (inbox, `/conversations`),
 * 6 (`/orders`) y 7 (CRM, `/crm`), y el paso 8 entero: Campañas (`/campaigns`), Plantillas
 * (`/templates`), Panel (`/dashboard`), Reportes (`/reports`, las seis tablas del módulo 10) y
 * Ayuda (`/help`), que fue la última y no consume la API. Las once secciones del menú tienen
 * pantalla real: ya no queda ninguna ruta con `PlaceholderPage` (el componente sigue en
 * `src/components/ui/`, sin uso, por si aparece una sección nueva).
 */
export const router = createBrowserRouter([
  {
    path: ROUTES.root,
    element: <RootRedirect />,
  },

  /* ----------------------------- Rutas públicas ----------------------------- */
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: ROUTES.login, element: <LoginPage /> },
          { path: ROUTES.register, element: <RegisterPage /> },
        ],
      },
    ],
  },

  /* ------------- Vitrina pública del catálogo (sin login) -------------- */
  {
    path: ROUTES.catalogo,
    element: <CatalogoPage />,
  },

  /* --------------------- Rutas protegidas (requieren JWT) -------------------- */
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            // Panel: el resumen ejecutivo del negocio activo — KPIs del resumen, serie diaria,
            // embudo, publicidad/CPA, horarios de atención y ranking de campañas. Los filtros de
            // fecha viven en la query string, igual que en Reportes.
            path: ROUTES.dashboard,
            element: <DashboardPage />,
          },
          {
            // Inbox: la bandeja (izquierda) y la conversación abierta (derecha), en una sola
            // pantalla. La conversación abierta vive en `?conversacion=<id>`.
            path: ROUTES.conversations,
            element: <InboxPage />,
          },
          {
            // Órdenes: el tablero del despacho — listado con filtros, ficha con historial,
            // cambio de estado y carga de la guía (que pasa la orden a ENVIADA y avisa al cliente).
            path: ROUTES.orders,
            element: <OrdersPage />,
          },
          {
            path: ROUTES.products,
            element: <ProductsPage />,
          },
          {
            // CRM: el embudo de leads. Kanban (cards por estado) y tabla (listado paginado),
            // con reclasificación manual para cualquier miembro y recálculo masivo solo para ADMIN.
            path: ROUTES.crm,
            element: <CrmPage />,
          },
          {
            path: ROUTES.campaigns,
            element: <CampaignsPage />,
          },
          {
            path: ROUTES.templates,
            element: <TemplatesPage />,
          },
          {
            // Reportes: las seis tablas del módulo 10 (resumen, serie, CPA por anuncio/campaña,
            // productos, motivos de no cierre y horarios). Mismos filtros en la URL que el panel.
            path: ROUTES.reports,
            element: <ReportsPage />,
          },
          {
            path: ROUTES.integrations,
            element: <IntegrationsPage />,
          },
          {
            // Ajustes en pestañas: cada una es una ruta propia (`/settings/general`,
            // `/settings/payments`, `/settings/team`) y la raíz redirige a la primera.
            path: ROUTES.settings,
            element: <SettingsPage />,
            children: [
              { index: true, element: <Navigate to={ROUTES.settingsGeneral} replace /> },
              { path: 'general', element: <SettingsGeneralTab /> },
              { path: 'payments', element: <PaymentMethodsTab /> },
              { path: 'team', element: <TeamTab /> },
            ],
          },
          {
            // Ayuda: la guía de uso del panel. NO consume la API — es contenido estático del propio
            // frontend, así que no hay endpoints que documentar acá.
            path: ROUTES.help,
            element: <HelpPage />,
          },
        ],
      },
    ],
  },

  /* -------------------------------- 404 ----------------------------------- */
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
